import { cva, type VariantProps } from "class-variance-authority";
import type { LucideIcon } from "lucide-react";
import { Slot } from "radix-ui";
import type * as React from "react";

import { cn } from "../lib/cn";

export const buttonVariants = cva(
  [
    "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-transparent",
    "font-medium transition-[background-color,border-color,color,box-shadow,filter,transform] duration-150",
    "outline-none focus-visible:shadow-ring active:translate-y-px",
    "disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  ],
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-xs hover:brightness-[1.08]",
        secondary: "bg-secondary text-secondary-foreground shadow-xs hover:bg-muted",
        outline:
          "border-border bg-card text-foreground shadow-xs hover:bg-accent hover:text-accent-foreground",
        ghost: "text-foreground hover:bg-accent hover:text-accent-foreground",
        destructive: "bg-destructive text-destructive-foreground shadow-xs hover:brightness-[1.08]",
        link: "h-auto px-0 text-primary underline underline-offset-4",
      },
      size: {
        default: "h-9 px-4 text-sm",
        sm: "h-8 gap-1.5 px-3 text-[13px]",
        lg: "h-11 rounded-lg px-6 text-[15px]",
        icon: "size-9",
        "icon-sm": "size-8",
      },
    },
    compoundVariants: [{ variant: "link", className: "h-auto px-0" }],
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends React.ComponentProps<"button">,
    VariantProps<typeof buttonVariants> {
  /** Render the child element (a link) with button styles. */
  asChild?: boolean;
  icon?: LucideIcon;
  iconRight?: LucideIcon;
}

/**
 * One `default` button per view, for the action the screen exists for; everything else is
 * outline, secondary or ghost. Labels start with a verb, sentence case.
 */
export function Button({
  className,
  variant,
  size,
  asChild = false,
  icon: Leading,
  iconRight: Trailing,
  children,
  type,
  ...props
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size }), className);
  if (asChild) {
    return (
      <Slot.Root data-slot="button" className={classes} {...props}>
        {Leading ? <Leading aria-hidden strokeWidth={1.75} /> : null}
        <Slot.Slottable>{children}</Slot.Slottable>
        {Trailing ? <Trailing aria-hidden strokeWidth={1.75} /> : null}
      </Slot.Root>
    );
  }
  return (
    <button data-slot="button" type={type ?? "button"} className={classes} {...props}>
      {Leading ? <Leading aria-hidden strokeWidth={1.75} /> : null}
      {children}
      {Trailing ? <Trailing aria-hidden strokeWidth={1.75} /> : null}
    </button>
  );
}
